import { db } from '@qcdoc/mock-runtime/helpers.ts';
import { adoptSystemChanges, saveDatabase } from '@qcdoc/mock-runtime/database.ts';
import { ensureAccessModel } from '@qcdoc/mock-runtime/accessControl.ts';
import { ensureAdministrationFixtures } from '@qcdoc/mock-runtime/adminFixtures.ts';
import { ensureAssignmentScopeModel } from '@qcdoc/mock-runtime/assignmentScopeModel.ts';
import { ensureRoleScopeModel } from '@qcdoc/mock-runtime/roleScopeModel.ts';
import { ensurePermissionGrantModel } from '@qcdoc/mock-runtime/permissionGrantModel.ts';
import { accessProfileFor } from '@qcdoc/mock-runtime/accessProfile.ts';
import type { AccessContext } from '@qcdoc/mock-runtime/contracts/accessModel.ts';
import { AUTH_PORTAL } from '../../app/portal.ts';
import type { AuthUser } from '../../api/auth/authApi.ts';
import { toUserInfo } from './authService.ts';
import type { StoredSession } from './persistence.ts';
import { classifyMockPersona, discoverMockPersonaGroups } from './mockPersonas.ts';
import { compatibilityGroupsFor, expandAssignedRoles } from './mockRoleGroups.ts';

export const MOCK_SSO_USER = 'noaccess@qcdoc.internal';
export const MOCK_TEST_ROLES = [
  'No Access',
  'Administrator', 'Administrator - Baroid', 'Administrator - Cementing', 'Administrator - PE', 'Administrator - Multi-Chem',
  'Global Quality Lead', 'Category Manager', 'QA Lab', 'QA Lab Supervisor', 'QA View',
  'Purchasing Agent', 'Manager', 'Project Manager', 'Group Leader', 'Logistics', 'Supply Chain', 'Regulatory',
  'Formula Editor', 'Hdfx', 'Quality Documenter', 'PSD', 'Assistant', 'Packaging Admin',
] as const;

export type MockTestRole = (typeof MOCK_TEST_ROLES)[number];

type MockAccount = (ReturnType<typeof db>['users'])[number];

const roleTestEmails: Record<MockTestRole, string> = {
  'No Access': 'noaccess@qcdoc.internal',
  Administrator: 'administrator@example.test',
  'Administrator - Baroid': 'administrator.baroid@example.test',
  'Administrator - Cementing': 'administrator.cementing@example.test',
  'Administrator - PE': 'administrator.pe@example.test',
  'Administrator - Multi-Chem': 'administrator.multi-chem@example.test',
  'Global Quality Lead': 'gql@example.test',
  'Category Manager': 'cm@example.test',
  'QA Lab': 'qalab@example.test',
  'QA Lab Supervisor': 'qalabsupervisor@example.test',
  'QA View': 'tester1@example.test',
  'Purchasing Agent': 'purchasingagent@example.test',
  Manager: 'manager@example.test',
  'Project Manager': 'projectmanager@example.test',
  'Group Leader': 'groupleader@example.test',
  Logistics: 'logistics@example.test',
  'Supply Chain': 'supplychain@example.test',
  Regulatory: 'regulatory@example.test',
  'Formula Editor': 'formulaeditor@example.test',
  Hdfx: 'hdfx@example.test',
  'Quality Documenter': 'qatech@example.test',
  PSD: 'productowner@example.test',
  Assistant: 'assistant@example.test',
  'Packaging Admin': 'packagingadmin@example.test',
};

export function mockTestRoleGroups(role: MockTestRole): string[] {
  return compatibilityGroupsFor(role);
}

export interface MockTestProfile {
  role: string;
  email: string;
  name: string;
  userId?: string;
  disabled?: boolean;
  roles?: string[];
}

export function mockTestProfiles(): MockTestProfile[] {
  const database = db();
  const fixed = MOCK_TEST_ROLES.map((role) => {
    const user = database.users.find((entry) => entry.portal === 'QCDOC' && entry.userName === roleTestEmails[role]);
    return {
      ...mockTestProfile(role),
      userId: user?.oid,
      disabled: !user || user.isApproved === false || user.isActive === false || user.isLockedOut === true,
    };
  });
  const fixedEmails = new Set(fixed.map((profile) => profile.email.toLowerCase()));
  const createdUsers = database.users.filter((user) => user.portal === 'QCDOC' && user.isMockTestAccount === true
    && user.isRoleTestProfile !== true && user.isMockPersonaProfile !== true
    && !String(user.description ?? '').toLowerCase().includes('legacy test profile')
    && !fixedEmails.has(String(user.email ?? user.userName).toLowerCase()));
  return [...fixed, ...createdUsers.map((user) => ({ role: `user:${user.oid}`, userId: user.oid,
    email: String(user.email ?? user.userName), name: String(user.fullName || user.userName),
    disabled: user.isApproved === false || user.isActive === false || user.isLockedOut === true,
    roles: Array.isArray(user.roles) ? user.roles.filter((role): role is string => typeof role === 'string') : [] }))];
}

export function mockTestProfile(role: MockTestRole): MockTestProfile {
  return {
    role,
    email: roleTestEmails[role],
    name: role,
  };
}

export function mockEntraSession(role: string = 'Administrator'): StoredSession {
  const database = db();
  const changed = [ensureAdministrationFixtures(database), ensureAccessModel(database), ensureAssignmentScopeModel(database),
    ensureRoleScopeModel(database), ensurePermissionGrantModel(database)].some(Boolean);
  if (changed) { adoptSystemChanges(); saveDatabase(); }
  const sessionForAccount = (account: MockAccount, options?: { accessProfile?: AccessContext; personaId?: string | null; groups?: string[]; packaging?: { read: boolean; write: boolean; admin: boolean } }): StoredSession => {
    if (!account.isApproved || account.isLockedOut) throw new Error('This test account is inactive or locked.');
    return {
      token: `mock-session:${String(account.userId ?? account.oid)}`,
      expiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
      user: { sub: String(account.userId ?? account.oid), email: String(account.email ?? account.userName),
        name: String(account.fullName ?? account.userName), preferredUsername: String(account.userName),
        personaId: options?.personaId === undefined
          ? classifyMockPersona(account as { portal?: string; roles?: unknown; accessProfile?: AccessContext | undefined })
          : options.personaId ?? undefined,
        groups: options?.groups ?? expandAssignedRoles(account.roles), portal: String(account.portal ?? 'QCDOC'),
        isInternal: String(account.portal ?? 'QCDOC').toUpperCase() !== 'QCDOC-VENDOR',
        packaging: options?.packaging ?? account.packaging as { read: boolean; write: boolean; admin: boolean } | undefined,
        accessProfile: options?.accessProfile ?? accessProfileFor(account) },
    };
  };
  if (role.startsWith('user:')) {
    const account = db().users.find((user) => user.oid === role.slice(5) && user.portal === AUTH_PORTAL
      && user.isApproved !== false && user.isActive !== false && !user.isLockedOut);
    if (!account) throw new Error('This test user is unavailable.');
    return sessionForAccount(account, { personaId: null });
  }
  if (role.startsWith('persona:')) {
    const personaId = role.slice('persona:'.length);
    const persona = discoverMockPersonaGroups(AUTH_PORTAL).find((group) => group.id === personaId);
    if (!persona) throw new Error('Unknown persona profile.');
    const account = persona.members.map((member) => db().users.find((user) => String(user.userId ?? user.oid) === member.userId
      && user.portal === AUTH_PORTAL && user.isApproved !== false && user.isActive !== false && !user.isLockedOut))
      .find((entry): entry is MockAccount => !!entry);
    if (!account) throw new Error('This persona is unavailable.');
    return sessionForAccount(account, { personaId: persona.id });
  }
  if (!MOCK_TEST_ROLES.includes(role as MockTestRole)) throw new Error('Unknown test profile.');
  const selectedRole = role as MockTestRole;
  if (role === 'No Access') {
    return {
      token: 'mock-session:noaccess',
      expiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
      user: {
        sub: 'mock-no-access-user', email: roleTestEmails[role], name: 'No Access',
        preferredUsername: roleTestEmails[role], groups: [], portal: 'QCDOC', personaId: undefined,
      },
    };
  }
  const account = db().users.find((record) => record.portal === 'QCDOC' && record.userName === roleTestEmails[selectedRole]);
  if (!account || typeof account.userId !== 'string') throw new Error('The fixed demo account is unavailable.');
  return sessionForAccount(account);
}

const VENDOR_PORTAL_ROLES = ['Vendor', 'VendorAdmin', 'SuperUser'];

export interface MockVendorSsoAccount {
  userId: string;
  name: string;
  email: string;
  company: string;
}

const vendorSsoEligible = (account: MockAccount): boolean => account.portal === 'QCDOC-VENDOR'
  && account.isApproved !== false && account.isActive !== false && !account.isLockedOut
  && expandAssignedRoles(account.roles).some((role) => VENDOR_PORTAL_ROLES.includes(role));

/** The one supplier account the simulated vendor Microsoft sign-in offers, like the internal fixed demo identity. */
export const MOCK_VENDOR_SSO_USER = 'demo.user@example.test';

/** The fixed supplier demo identity; falls back to the first eligible supplier if that account was removed. */
export function mockVendorSsoAccount(): MockVendorSsoAccount | undefined {
  const eligible = db().users.filter(vendorSsoEligible);
  const account = eligible.find((entry) => String(entry.email ?? entry.userName).toLowerCase() === MOCK_VENDOR_SSO_USER)
    ?? [...eligible].sort((left, right) => String(left.company ?? '').localeCompare(String(right.company ?? '')) || String(left.userName).localeCompare(String(right.userName)))[0];
  return account && {
    userId: String(account.userId ?? account.oid),
    name: String(account.fullName ?? account.userName),
    email: String(account.email ?? account.userName),
    company: String(account.company ?? ''),
  };
}

export interface MockVendorSwitchAccount extends MockVendorSsoAccount {
  userName: string;
}

/** The legacy vendor logins (IsVendor accounts) the vendor portal prototype can switch between, one per supplier. */
export function mockVendorSwitchAccounts(): MockVendorSwitchAccount[] {
  return db().users.filter((account) => account.isLegacyVendorLogin === true && vendorSsoEligible(account)).map((account) => ({
    userId: String(account.userId ?? account.oid),
    userName: String(account.userName),
    name: String(account.fullName ?? account.userName),
    email: String(account.email ?? account.userName),
    company: String(account.company ?? ''),
  }));
}

/** The supplier account whose email matches the Microsoft account, else the fixed supplier demo account that provides company data. */
export function mockVendorSsoAccountFor(email?: string): MockVendorSsoAccount | undefined {
  const wanted = email?.trim().toLowerCase();
  const match = wanted ? db().users.filter(vendorSsoEligible).find((entry) => [entry.email, entry.userName].some((value) => String(value ?? '').toLowerCase() === wanted)) : undefined;
  if (!match) return mockVendorSsoAccount();
  return {
    userId: String(match.userId ?? match.oid),
    name: String(match.fullName ?? match.userName),
    email: String(match.email ?? match.userName),
    company: String(match.company ?? ''),
  };
}

/** Session for a supplier account, shaped like the credential sign-in result so every vendor screen behaves the same. */
export function mockVendorSsoSession(userId: string, identity?: { name?: string; email?: string }): StoredSession {
  const account = db().users.find((record) => String(record.userId ?? record.oid) === userId);
  if (!account || !vendorSsoEligible(account)) throw new Error('This supplier account is unavailable or has no vendor portal access.');
  const user: AuthUser = {
    userId: String(account.userId ?? account.oid),
    userName: String(account.userName),
    email: account.email === undefined ? undefined : String(account.email),
    fullName: String(account.fullName ?? account.userName),
    company: account.company === undefined ? undefined : String(account.company),
    psl: account.psl === undefined ? undefined : String(account.psl),
    isInternal: false,
    portal: 'QCDOC-VENDOR',
    packaging: account.packaging as AuthUser['packaging'],
    roles: Array.isArray(account.roles) ? account.roles.filter((role): role is string => typeof role === 'string') : [],
  };
  return {
    token: `mock-session:${user.userId}`,
    expiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
    user: { ...toUserInfo(user), ...(identity?.name ? { name: identity.name } : {}),
      ...(identity?.email ? { email: identity.email, preferredUsername: identity.email } : {}) },
  };
}